import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import api from '../utils/api';
import ListingCard from '../components/listings/ListingCard';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { useState } from 'react';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);

  const q = searchParams.get('q') || '';
  const type = searchParams.get('type') || '';
  const city = searchParams.get('city') || '';
  const minPrice = searchParams.get('min_price') || '';
  const maxPrice = searchParams.get('max_price') || '';

  const { data, isLoading } = useQuery(
    ['search', q, type, city, minPrice, maxPrice],
    () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (type) params.set('listing_type', type);
      if (city) params.set('city', city);
      if (minPrice) params.set('min_price', minPrice);
      if (maxPrice) params.set('max_price', maxPrice);
      params.set('limit', '20');
      return api.get(`/search?${params}`).then(r => r.data);
    }
  );

  const setParam = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
  };

  const listings = data?.listings || [];
  const total = data?.total || 0;

  return (
    <div className="min-h-screen pt-20 pb-12" dir="rtl">
      <div className="max-w-7xl mx-auto px-4">
        {/* Search Bar */}
        <div className="glass rounded-2xl p-4 mb-6 flex gap-3 items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              defaultValue={q}
              onKeyDown={e => e.key === 'Enter' && setParam('q', e.target.value)}
              className="form-input w-full pr-10"
              placeholder="ابحث عن ملاعب، معدات، خدمات..."
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-colors ${showFilters ? 'bg-primary-500/20 border-primary-500/50 text-primary-400' : 'bg-slate-700/50 border-slate-600 text-slate-400'}`}>
            <SlidersHorizontal size={16} />
            فلاتر
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="glass rounded-2xl p-5 mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1.5">النوع</label>
              <select value={type} onChange={e => setParam('type', e.target.value)} className="form-input w-full text-sm py-2">
                <option value="">الكل</option>
                <option value="venue">ملاعب/منشآت</option>
                <option value="equipment">معدات</option>
                <option value="service">خدمات</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1.5">المدينة</label>
              <input value={city} onChange={e => setParam('city', e.target.value)} className="form-input w-full text-sm py-2" placeholder="جميع المدن" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1.5">الحد الأدنى للسعر</label>
              <input type="number" value={minPrice} onChange={e => setParam('min_price', e.target.value)} className="form-input w-full text-sm py-2" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1.5">الحد الأقصى للسعر</label>
              <input type="number" value={maxPrice} onChange={e => setParam('max_price', e.target.value)} className="form-input w-full text-sm py-2" placeholder="بلا حد" />
            </div>
          </div>
        )}

        {/* Active Filters */}
        <div className="flex flex-wrap gap-2 mb-5">
          {q && <span className="flex items-center gap-1.5 bg-primary-500/20 text-primary-400 text-sm px-3 py-1 rounded-full border border-primary-500/30">"{q}" <button onClick={() => setParam('q', '')}><X size={12} /></button></span>}
          {type && <span className="flex items-center gap-1.5 bg-primary-500/20 text-primary-400 text-sm px-3 py-1 rounded-full border border-primary-500/30">{{ venue: 'ملاعب', equipment: 'معدات', service: 'خدمات' }[type]} <button onClick={() => setParam('type', '')}><X size={12} /></button></span>}
          {city && <span className="flex items-center gap-1.5 bg-primary-500/20 text-primary-400 text-sm px-3 py-1 rounded-full border border-primary-500/30">{city} <button onClick={() => setParam('city', '')}><X size={12} /></button></span>}
        </div>

        {/* Results */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold">
            {isLoading ? 'جارٍ البحث...' : `${total} نتيجة`}
            {q && <span className="text-slate-400 font-normal"> لـ "{q}"</span>}
          </h2>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="skeleton h-72 rounded-2xl" />)}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <Search size={56} className="text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">لا توجد نتائج</h3>
            <p className="text-slate-400">جرّب كلمات بحث مختلفة أو غيّر الفلاتر</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {listings.map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        )}
      </div>
    </div>
  );
}
